/**
 * Foundation tokens: the brand palette and the platform-neutral scales. Shared by admin web (MUI
 * theme, P03) and mobile (P04).
 *
 * Q-29 default: keep the legacy layout and navigation; refresh spacing and typography with these
 * tokens. Brand gold and navy were sampled from the legacy screenshots in
 * docs/source/lions-features.pdf (JPEG, so approximate). Values without a confirmed source are
 * listed in PLACEHOLDER_TOKENS; replace them once the client supplies the brand guide.
 *
 * Numbers are unitless: px on web, density-independent points on mobile.
 */
export const palette = {
    /** Lions gold/yellow: primary actions, active tabs, highlights. Sampled #F8B818. */
    gold: {
        50: '#FFF8E1',
        100: '#FEECB3',
        200: '#FCDF80',
        300: '#FAD24D',
        400: '#F9C42A',
        500: '#F8B818',
        600: '#E0A414',
        700: '#BF8A0F',
        800: '#9C700B',
        900: '#6E4F06',
        /** Derived (DS-01): deepest shade, the dark-theme primary-subtle surface. */
        950: '#3F2D03',
    },
    /** Lions navy: headers, navigation, text on gold. Sampled #102050. */
    navy: {
        50: '#E7EAF2',
        100: '#C3CADF',
        200: '#9BA7C9',
        300: '#7384B3',
        400: '#4C5F99',
        500: '#2A3D78',
        600: '#1C2D64',
        700: '#102050',
        800: '#0A1840',
        900: '#06102B',
        /** Derived (DS-01): deepest shade. */
        950: '#030817',
    },
    neutral: {
        0: '#FFFFFF',
        50: '#F7F8FA',
        100: '#EEF0F4',
        200: '#DDE1E8',
        300: '#C4CAD4',
        400: '#9AA2B1',
        500: '#717A8C',
        /** Derived (DS-01): subtle text that still passes AA on the light page background. */
        550: '#636B7C',
        600: '#535B6B',
        700: '#3A404D',
        800: '#242833',
        /** Derived (DS-01): dark-theme surface. */
        850: '#1B1E26',
        900: '#12141A',
        /** Derived (DS-01): dark-theme sunken surface. */
        950: '#0C0E12',
    },
    success: '#2E7D32',
    warning: '#ED6C02',
    error: '#D32F2F',
    info: '#0288D1',
};
/** Semantic colours; components use these, never the raw palette. */
export const colors = {
    primary: palette.gold[500],
    primaryHover: palette.gold[600],
    primaryContrast: palette.navy[700],
    secondary: palette.navy[700],
    secondaryHover: palette.navy[600],
    secondaryContrast: palette.neutral[0],
    background: palette.neutral[0],
    surface: palette.neutral[50],
    surfaceInverse: palette.navy[700],
    border: palette.neutral[200],
    textPrimary: palette.neutral[900],
    textSecondary: palette.neutral[600],
    textInverse: palette.neutral[0],
    textDisabled: palette.neutral[400],
    success: palette.success,
    warning: palette.warning,
    error: palette.error,
    info: palette.info,
    /** Admin table header row (the legacy lavender header). */
    tableHeader: palette.navy[100],
    /** Admin date-range pill. The legacy pill is a brighter green; darkened so white text passes AA. */
    filterPill: palette.success,
    filterPillContrast: palette.neutral[0],
    /** Destructive actions (delete buttons and icons). */
    danger: palette.error,
    dangerContrast: palette.neutral[0],
};
export const fontFamilies = {
    /** Headings. PLACEHOLDER: brand display font unknown. */
    heading: 'Montserrat',
    /** Body and UI text. PLACEHOLDER: brand text font unknown. */
    body: 'Inter',
    mono: 'JetBrains Mono',
};
export const fontWeights = {
    regular: 400,
    medium: 500,
    semibold: 600,
    bold: 700,
};
/** Type scale: fontSize / lineHeight in px (web) or points (mobile). */
export const typography = {
    display: { fontFamily: fontFamilies.heading, fontSize: 32, lineHeight: 40, fontWeight: 700 },
    h1: { fontFamily: fontFamilies.heading, fontSize: 28, lineHeight: 36, fontWeight: 700 },
    h2: { fontFamily: fontFamilies.heading, fontSize: 24, lineHeight: 32, fontWeight: 700 },
    h3: { fontFamily: fontFamilies.heading, fontSize: 20, lineHeight: 28, fontWeight: 600 },
    h4: { fontFamily: fontFamilies.heading, fontSize: 18, lineHeight: 24, fontWeight: 600 },
    bodyLarge: { fontFamily: fontFamilies.body, fontSize: 16, lineHeight: 24, fontWeight: 400 },
    body: { fontFamily: fontFamilies.body, fontSize: 14, lineHeight: 20, fontWeight: 400 },
    label: { fontFamily: fontFamilies.body, fontSize: 14, lineHeight: 20, fontWeight: 600 },
    caption: { fontFamily: fontFamilies.body, fontSize: 12, lineHeight: 16, fontWeight: 400 },
    overline: { fontFamily: fontFamilies.body, fontSize: 11, lineHeight: 16, fontWeight: 600 },
};
/** 4-point spacing scale. */
export const spacing = {
    none: 0,
    xxs: 2,
    xs: 4,
    sm: 8,
    md: 12,
    lg: 16,
    xl: 24,
    xxl: 32,
    xxxl: 48,
};
export const radius = {
    none: 0,
    sm: 4,
    md: 8,
    lg: 12,
    xl: 16,
    pill: 999,
};
/**
 * Derived status tints (DS-01). The base colours in `palette` never change; these exist because
 * the bases fail WCAG AA as text on some surfaces. `text` is the readable colour on a surface,
 * `subtle` the tinted background for badges and alerts. Dark-theme text tints were approved with
 * DS-01; light-theme text tints darken every status colour for AA on the grey page background.
 */
export const statusTints = {
    success: {
        lightText: '#2A752E',
        lightSubtle: '#E8F5E9',
        darkText: '#66BB6A',
        darkSubtle: '#16281A',
    },
    warning: {
        lightText: '#A64200',
        lightSubtle: '#FFF4E5',
        darkText: '#FFA726',
        darkSubtle: '#2E2010',
    },
    error: {
        lightText: '#C62828',
        lightSubtle: '#FDECEA',
        darkText: '#F47067',
        darkSubtle: '#2E1719',
    },
    info: {
        lightText: '#01579B',
        lightSubtle: '#E5F6FD',
        darkText: '#4FC3F7',
        darkSubtle: '#0E2430',
    },
};
//# sourceMappingURL=foundation.js.map